import { h, Component } from 'preact';
import { createPortal } from 'preact/compat';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import dialogStore from '../../stores/dialog';
import { watchDialogBackButton } from '../../lib/dialogLifecycle';
import './style.css';

class DialogContent extends Component {
    constructor(props) {
        super(props);
        this.state = { value: props.request.defaultValue };
        this.composing = false;
    }

    accept = event => {
        if (this.composing || event?.detail > 1) return;
        const { request, store } = this.props;
        const value = request.kind === 'prompt' ? this.state.value : request.kind === 'confirm' ? true : undefined;
        store.respond(request.id, value);
    }

    cancel = event => {
        if (event?.detail > 1) return;
        this.props.store.dismiss(this.props.request.id);
    }

    render({ request }, { value }) {
        const { id, kind, message, title, confirmLabel, cancelLabel } = request;
        const titleId = `app-dialog-title-${id}`;
        const messageId = `app-dialog-message-${id}`;
        const inputId = `app-dialog-input-${id}`;
        return (
            <div class='app-dialog-backdrop'>
                <div
                    class='app-dialog bg-body text-body border rounded-3 shadow-lg p-4'
                    role={kind === 'alert' ? 'alertdialog' : 'dialog'}
                    aria-modal='true'
                    aria-labelledby={titleId}
                    aria-describedby={messageId}
                    tabIndex={-1}
                    ref={element => { this.panel = element; }}
                >
                    <h2 class='h5 mb-3' id={titleId}>
                        {title != null ? String(title) : kind === 'alert' ? (
                            <Text id='dialogs.alertTitle'>Message</Text>
                        ) : kind === 'confirm' ? (
                            <Text id='dialogs.confirmTitle'>Confirm</Text>
                        ) : (
                            <Text id='dialogs.promptTitle'>Enter a value</Text>
                        )}
                    </h2>
                    <div class='app-dialog-message mb-3' id={messageId}>{message}</div>
                    {kind === 'prompt' && (
                        <div class='mb-3'>
                            <label class='form-label visually-hidden' htmlFor={inputId}>
                                {message || <Text id='dialogs.valueLabel'>Value</Text>}
                            </label>
                            <input
                                id={inputId}
                                class='form-control'
                                type='text'
                                value={value}
                                ref={element => { this.input = element; }}
                                onInput={event => this.setState({ value: event.currentTarget.value })}
                                onCompositionStart={() => { this.composing = true; }}
                                onCompositionEnd={() => { this.composing = false; }}
                            />
                        </div>
                    )}
                    <div class='d-flex flex-wrap justify-content-end gap-2'>
                        {kind !== 'alert' && (
                            <button
                                type='button'
                                class='btn btn-outline-secondary'
                                onClick={this.cancel}
                                ref={element => { this.cancelButton = element; }}
                            >
                                {cancelLabel != null ? String(cancelLabel) : <Text id='dialogs.cancel'>Cancel</Text>}
                            </button>
                        )}
                        <button
                            type='button'
                            class='btn btn-primary'
                            onClick={this.accept}
                            ref={element => { this.confirmButton = element; }}
                        >
                            {confirmLabel != null ? String(confirmLabel) : <Text id='dialogs.ok'>OK</Text>}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

@observer
class DialogHost extends Component {
    constructor(props) {
        super(props);
        this.portal = typeof document === 'undefined' ? null : document.createElement('div');
        this.active = false;
        this.activeId = null;
    }

    get store() {
        return this.props.store || dialogStore;
    }

    componentDidMount() {
        document.body.appendChild(this.portal);
        this.store.attach(this);
        this.syncDialog();
    }

    componentDidUpdate() {
        this.syncDialog();
    }

    componentWillUnmount() {
        this.store.detach(this);
        this.release(false);
        this.portal.remove();
    }

    focusInitial = () => {
        if (!this.content) return;
        const { input, cancelButton, confirmButton, panel } = this.content;
        (input || cancelButton || confirmButton || panel)?.focus();
    }

    handleFocus = event => {
        if (this.store.requests.length && this.content?.panel && !this.content.panel.contains(event.target)) {
            event.stopImmediatePropagation();
            this.focusInitial();
        }
    }

    handleKey = event => {
        const request = this.store.current;
        if (!request || !this.content?.panel) return;
        event.stopImmediatePropagation();
        if (event.isComposing || this.content.composing || event.keyCode === 229) return;
        if ((event.key === 'Escape' || event.key === 'Enter') && event.repeat) {
            event.preventDefault();
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            this.store.dismiss(request.id);
        } else if (event.key === 'Enter' && event.target === this.content.input) {
            event.preventDefault();
            this.content.accept(event);
        } else if (event.key === 'Tab') {
            const controls = Array.from(this.content.panel.querySelectorAll('button:not(:disabled), input'));
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && (event.target === first || !controls.includes(event.target))) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && (event.target === last || !controls.includes(event.target))) {
                event.preventDefault();
                first?.focus();
            }
        }
    }

    captureKey = event => {
        if (this.store.current) event.stopImmediatePropagation();
    }

    updateViewport = () => {
        const viewport = window.visualViewport;
        if (!viewport) return;
        this.portal.style.setProperty('--app-dialog-height', `${viewport.height}px`);
        this.portal.style.setProperty('--app-dialog-top', `${viewport.offsetTop}px`);
    }

    syncDialog() {
        const request = this.store.current;
        if (!request) {
            this.release(this.store.restoreFocus);
            return;
        }
        if (!this.active) {
            this.active = true;
            this.previousFocus = document.activeElement;
            this.background = Array.from(document.body.children)
                .filter(element => element !== this.portal)
                .map(element => ({
                    element,
                    inert: element.getAttribute('inert'),
                    hidden: element.getAttribute('aria-hidden'),
                }));
            this.previousOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            // Move focus before hiding its previous ancestor from assistive technology.
            this.focusInitial();
            this.background.forEach(({ element }) => {
                element.setAttribute('inert', '');
                element.setAttribute('aria-hidden', 'true');
            });
            window.addEventListener('keydown', this.handleKey, true);
            window.addEventListener('keyup', this.captureKey, true);
            window.addEventListener('keypress', this.captureKey, true);
            window.addEventListener('focusin', this.handleFocus, true);
            window.visualViewport?.addEventListener('resize', this.updateViewport);
            window.visualViewport?.addEventListener('scroll', this.updateViewport);
            this.stopBack = watchDialogBackButton({ store: this.store });
            this.updateViewport();
        }
        if (request.id !== this.activeId) {
            this.activeId = request.id;
            this.focusInitial();
        }
    }

    release(restoreFocus) {
        if (!this.active) return;
        this.active = false;
        this.activeId = null;
        window.removeEventListener('keydown', this.handleKey, true);
        window.removeEventListener('keyup', this.captureKey, true);
        window.removeEventListener('keypress', this.captureKey, true);
        window.removeEventListener('focusin', this.handleFocus, true);
        window.visualViewport?.removeEventListener('resize', this.updateViewport);
        window.visualViewport?.removeEventListener('scroll', this.updateViewport);
        this.stopBack?.();
        this.background.forEach(({ element, inert, hidden }) => {
            if (inert === null) element.removeAttribute('inert');
            else element.setAttribute('inert', inert);
            if (hidden === null) element.removeAttribute('aria-hidden');
            else element.setAttribute('aria-hidden', hidden);
        });
        if (document.body.style.overflow === 'hidden') document.body.style.overflow = this.previousOverflow;
        if (restoreFocus && this.previousFocus?.isConnected) this.previousFocus.focus();
        this.background = [];
        this.previousFocus = null;
    }

    render() {
        const request = this.store.current;
        if (!this.portal || !request) return null;
        return createPortal(
            <DialogContent
                key={request.id}
                request={request}
                store={this.store}
                ref={content => { this.content = content; }}
            />,
            this.portal,
        );
    }
}

export default DialogHost;
